import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { Loader2, Trash2, Plus, Save, Database, Sparkles } from "lucide-react";

/**
 * V232.6 — Admin tab for the ScanAdsTicker case studies.
 *
 * CRUD for `case_studies` collection. Admin can:
 *   • seed the 8 default rows (one-shot, idempotent)
 *   • add new rows
 *   • inline-edit any field
 *   • toggle enabled per row
 *   • delete rows
 *
 * No drag-to-reorder yet (keeps the UI simple); slot values are
 * edited directly as integers. We can add dnd-kit later if needed.
 */
export default function AdminCaseStudiesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);
  const [adminToken, setAdminToken] = useState(() => {
    try { return localStorage.getItem("admin_token") || ""; } catch { return ""; }
  });

  const headers = () => ({ "X-Admin-Token": adminToken });

  const refresh = async () => {
    if (!adminToken) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/case-studies`, { headers: headers() });
      setItems(r.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Load failed");
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  const seed = async () => {
    try {
      await axios.post(`${API}/admin/case-studies/seed`, {}, { headers: headers() });
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message);
    }
  };

  const addNew = async () => {
    const nextSlot = Math.max(0, ...items.map((r) => r.slot || 0)) + 1;
    try {
      await axios.post(`${API}/admin/case-studies`, {
        domain: "nova-domena.sk",
        industry: "Odvetvie",
        pct: 25,
        before: 50,
        after: 62,
        quote: "Krátky Slovak testimonial…",
        slot: nextSlot,
        enabled: true,
      }, { headers: headers() });
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message);
    }
  };

  const patchRow = async (id, patch) => {
    setSavingId(id);
    try {
      await axios.patch(`${API}/admin/case-studies/${id}`, patch, { headers: headers() });
      // Optimistic local update
      setItems((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message);
    }
    setSavingId(null);
  };

  const deleteRow = async (id) => {
    if (!confirm("Odstrániť túto case study?")) return;
    try {
      await axios.delete(`${API}/admin/case-studies/${id}`, { headers: headers() });
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-case-studies-tab">
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Database className="w-5 h-5 text-purple-600" />
              Scan Ads Case Studies
            </h2>
            <p className="text-sm text-gray-500 mt-1 max-w-2xl">
              Rotujúce success-story karty zobrazované návštevníkom počas čakania
              na výsledok skenu. Edituj doménu, % nárast, pred/po čísla a citát.
              Zmeny platia pre nové skeny do 60s (cache).
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={seed}
              data-testid="case-studies-seed-btn"
              className="text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 flex items-center gap-1"
            >
              <Sparkles className="w-3.5 h-3.5" /> Seed 8 default
            </button>
            <button
              type="button"
              onClick={addNew}
              data-testid="case-studies-add-btn"
              className="text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Pridať
            </button>
          </div>
        </div>

        {!adminToken && (
          <div className="mb-3">
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">
              Admin Token
            </label>
            <input
              type="password"
              value={adminToken}
              onChange={(e) => {
                setAdminToken(e.target.value);
                try { localStorage.setItem("admin_token", e.target.value); } catch { /* ignore */ }
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-900 placeholder-gray-400"
              placeholder="X-Admin-Token"
              data-testid="case-studies-admin-token"
            />
          </div>
        )}

        {error && (
          <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="case-studies-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="py-8 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500" data-testid="case-studies-empty">
            Žiadne case studies. Klikni <strong>Seed 8 default</strong> alebo <strong>Pridať</strong>.
          </div>
        ) : (
          <div className="space-y-3" data-testid="case-studies-list">
            {items.map((row) => (
              <CaseStudyRow
                key={row.id}
                row={row}
                saving={savingId === row.id}
                onPatch={patchRow}
                onDelete={deleteRow}
              />
            ))}
          </div>
        )}

        {items.length > 0 && (
          <p className="text-xs text-gray-400 mt-4">
            {items.filter((r) => r.enabled).length} aktívnych / {items.length} celkom. Public ticker používa iba enabled riadky, triedené podľa slot.
          </p>
        )}
      </div>
    </div>
  );
}

function CaseStudyRow({ row, saving, onPatch, onDelete }) {
  const [local, setLocal] = useState(row);
  const dirty = JSON.stringify(local) !== JSON.stringify(row);

  const setField = (k, v) => setLocal((l) => ({ ...l, [k]: v }));

  const save = () => {
    const diff = {};
    for (const [k, v] of Object.entries(local)) {
      if (row[k] !== v && k !== "id" && k !== "created_at" && k !== "updated_at") {
        diff[k] = v;
      }
    }
    if (Object.keys(diff).length) onPatch(row.id, diff);
  };

  return (
    <div
      className={`border rounded-xl p-4 ${local.enabled ? "border-gray-200 bg-white" : "border-gray-100 bg-gray-50 opacity-70"}`}
      data-testid={`case-study-row-${row.id}`}
    >
      <div className="grid grid-cols-12 gap-2 items-start">
        <div className="col-span-12 sm:col-span-3">
          <Field label="Doména" value={local.domain} onChange={(v) => setField("domain", v)} testid={`cs-domain-${row.id}`} />
        </div>
        <div className="col-span-12 sm:col-span-3">
          <Field label="Odvetvie" value={local.industry} onChange={(v) => setField("industry", v)} testid={`cs-industry-${row.id}`} />
        </div>
        <div className="col-span-4 sm:col-span-1">
          <Field label="Slot" type="number" value={local.slot} onChange={(v) => setField("slot", parseInt(v) || 0)} testid={`cs-slot-${row.id}`} />
        </div>
        <div className="col-span-4 sm:col-span-1">
          <Field label="+%" type="number" value={local.pct} onChange={(v) => setField("pct", parseInt(v) || 0)} testid={`cs-pct-${row.id}`} />
        </div>
        <div className="col-span-4 sm:col-span-1">
          <Field label="Pred" type="number" value={local.before} onChange={(v) => setField("before", parseInt(v) || 0)} testid={`cs-before-${row.id}`} />
        </div>
        <div className="col-span-6 sm:col-span-1">
          <Field label="Po" type="number" value={local.after} onChange={(v) => setField("after", parseInt(v) || 0)} testid={`cs-after-${row.id}`} />
        </div>
        <div className="col-span-6 sm:col-span-2 flex items-end justify-end gap-1">
          <button
            type="button"
            onClick={() => onPatch(row.id, { enabled: !local.enabled })}
            data-testid={`cs-toggle-${row.id}`}
            className={`px-2 py-1 text-[10px] font-black uppercase tracking-wider rounded ${
              local.enabled
                ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                : "bg-gray-200 text-gray-600 hover:bg-gray-300"
            }`}
          >
            {local.enabled ? "ON" : "OFF"}
          </button>
          <button
            type="button"
            onClick={() => onDelete(row.id)}
            data-testid={`cs-delete-${row.id}`}
            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
            aria-label="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        <div className="col-span-12">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Citát</label>
          <textarea
            value={local.quote || ""}
            onChange={(e) => setField("quote", e.target.value)}
            rows={2}
            className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm bg-white text-gray-900 placeholder-gray-400"
            data-testid={`cs-quote-${row.id}`}
          />
        </div>
        {dirty && (
          <div className="col-span-12 flex justify-end">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid={`cs-save-${row.id}`}
              className="px-3 py-1.5 bg-purple-600 text-white font-bold text-xs rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Uložiť zmeny
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm bg-white text-gray-900 placeholder-gray-400"
        data-testid={testid}
      />
    </div>
  );
}
